class UnifiedPriceManager {
  constructor() {
    this.currentVariant = null;
    this.currentQuantity = 1;
    this.warrantyPrice = 0;
    this.warrantyComparePrice = 0;
    this.selectedWarrantyPlan = null;
    this.isInitialized = false;
    this.isProcessingEvent = false;
    this.lastProcessedVariantId = null;
    this.eventQueue = [];
    this.isProcessingQueue = false;

    this.componentSelectors = [
      { selector: '[data-dynamic-price="true"]', type: "custom-price" },
      { selector: '[data-total-price-row="true"]', type: "total-price-row" },
      { selector: "sticky-atc-bar", type: "sticky-atc-bar" },
      { selector: "[data-warranty-selector]", type: "warranty-selector" },
      { selector: "belffin-protection-popup", type: "belffin-protection-plan" },
    ];

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.registerExistingComponents();
    this.isInitialized = true;
    setTimeout(() => {
      this.initializeCurrentPrice();
    }, 500);
  }

  setupEventListeners() {
    document.addEventListener("productNewMediaUpdated", (event) => {
      this.handleProductNewMediaUpdated(event);
    });

    document.addEventListener("quantity:change", (event) => {
      if (event.detail && event.detail.value !== undefined) {
        const isFromCartDrawer = this.isEventFromCartDrawer(event);
        if (isFromCartDrawer) {
          return;
        }

        this.currentQuantity = event.detail.value;
        this.calculateAndUpdateAllPrices();
      }
    });

    document.addEventListener("warranty-selection-changed", (event) => {
      if (event.detail) {
        this.warrantyPrice =
          event.detail.warrantyPrice || event.detail.price || 0;
        this.warrantyComparePrice =
          event.detail.warrantyComparePrice || event.detail.comparePrice || 0;
        this.calculateAndUpdateAllPrices();
      }
    });

    document.addEventListener("variant:change", (event) => {
      if (event.detail && event.detail.variant) {
        this.handleVariantChange(event.detail.variant);
      }
    });

    document.addEventListener("change", (event) => {
      if (event.target.name === "warranty-product") {
        this.calculateAndUpdateAllPrices();
      }
    });
  }

  handleProductNewMediaUpdated(event) {
    if (this.isProcessingEvent) {
      return;
    }

    this.isProcessingEvent = true;

    try {
      const { newMedia, combination } = event.detail || {};
      if (!newMedia || !newMedia.currentCombination) {
        return;
      }
      const currentCombination = newMedia.currentCombination;
      const variant = currentCombination.variant;

      if (!variant || !variant.id) {
        return;
      }

      if (this.lastProcessedVariantId === variant.id) {
        return;
      }
      this.currentVariant = variant;
      this.lastProcessedVariantId = variant.id;
      this.calculateAndUpdateAllPrices();
    } catch (error) {
    } finally {
      setTimeout(() => {
        this.isProcessingEvent = false;
      }, 100);
    }
  }

  handleVariantChange(variant) {
    if (!variant || !variant.id) {
      return;
    }
    if (this.lastProcessedVariantId === variant.id) {
      return;
    }

    this.currentVariant = variant;
    this.lastProcessedVariantId = variant.id;
    this.calculateAndUpdateAllPrices();
  }

  registerExistingComponents() {
    this.componentSelectors.forEach(({ selector, type }) => {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        elements.forEach((element) => {
          this.registerComponent(type, element);
        });

        if (
          type === "warranty-selector" ||
          type === "belffin-protection-plan"
        ) {
        }
      }
    });
  }

  registerComponent(type, element) {
    if (element) {
      element.dataset.priceComponentType = type;
      element.dataset.priceComponentRegistered = "true";
    }
  }

  calculateAndUpdateAllPrices() {
    if (!this.currentVariant) {
      return;
    }
    const prices = this.calculatePrices();
    this.dispatchPriceUpdateEvent(prices);
    this.updateDOMElements(prices);
  }

  calculatePrices() {
    const productPrice = this.getProductPrice();
    const productComparePrice = this.getProductComparePrice();
    const productPriceInCents = this.getProductPriceInCents();
    const productComparePriceInCents = this.getProductComparePriceInCents();
    const productPriceNum = parseFloat(productPrice.replace("$", "")) || 0;
    const productComparePriceNum =
      parseFloat(productComparePrice.replace("$", "")) || 0;

    const selectedWarrantyPrice = this.getSelectedWarrantyPrice();
    const warrantyPriceNum = selectedWarrantyPrice.price / 100;
    const warrantyComparePriceNum = selectedWarrantyPrice.comparePrice / 100;

    const basePriceWithoutWarranty = productPriceNum;
    const totalPriceNum = basePriceWithoutWarranty * this.currentQuantity;

    const baseComparePriceWithoutWarranty = productComparePriceNum;
    const totalComparePriceNum =
      baseComparePriceWithoutWarranty * this.currentQuantity;

    return {
      customPrice: productPrice,
      customComparePrice: productComparePrice,
      currentCents: productPriceInCents,
      compareCents: productComparePriceInCents,
      warrantyPrice: warrantyPriceNum,
      warrantyComparePrice: warrantyComparePriceNum,
      quantity: this.currentQuantity,
      totalPrice: `$${totalPriceNum.toFixed(2)}`,
      totalPriceNum: totalPriceNum,
      totalComparePrice: `$${totalComparePriceNum.toFixed(2)}`,
      totalComparePriceNum: totalComparePriceNum,
      totalPriceFormatted: `$${totalPriceNum.toFixed(2)}`,
      totalComparePriceFormatted: `$${totalComparePriceNum.toFixed(2)}`,
      selectedWarrantyInfo: selectedWarrantyPrice,
    };
  }

  getProductPrice() {
    const productPriceInCents = this.getProductPriceInCents();

    if (productPriceInCents > 0) {
      const priceInDollars = productPriceInCents / 100;
      return `$${priceInDollars.toFixed(2)}`;
    }

    return "$0.00";
  }

  getProductComparePrice() {
    if (
      this.currentVariant &&
      this.currentVariant.compare_at_price !== undefined &&
      this.currentVariant.compare_at_price !== null &&
      this.currentVariant.compare_at_price !== ""
    ) {
      const comparePriceStr = this.currentVariant.compare_at_price.toString();
      if (comparePriceStr.includes(".")) {
        const comparePriceInDollars = parseFloat(comparePriceStr);
        return `$${comparePriceInDollars.toFixed(2)}`;
      } else {
        const comparePriceInCents = parseFloat(
          this.currentVariant.compare_at_price
        );
        const comparePriceInDollars = comparePriceInCents / 100;
        return `$${comparePriceInDollars.toFixed(2)}`;
      }
    }

    return "$0.00";
  }
  calculateWarrantyPrices() {
    const warrantyItems = document.querySelectorAll("[data-warranty-item]");
    let threeYearPrice = 0;
    let fiveYearPrice = 0;
    let threeYearFormatted = "--";
    let fiveYearFormatted = "--";

    warrantyItems.forEach((item) => {
      const planId = item.dataset.planId;
      const inputElement = item.querySelector(".warranty-selector__input");

      if (inputElement) {
        const price = Number(inputElement.dataset.price) || 0;
        const priceFormatted = inputElement.dataset.priceFormatted || "--";

        if (planId === "warranty-plan-1") {
          threeYearPrice = price;
          threeYearFormatted = priceFormatted;
        } else if (planId === "warranty-plan-2") {
          fiveYearPrice = price;
          fiveYearFormatted = priceFormatted;
        }
      }
    });

    return {
      threeYear: threeYearPrice,
      fiveYear: fiveYearPrice,
      threeYearFormatted: threeYearFormatted,
      fiveYearFormatted: fiveYearFormatted,
    };
  }

  getProductPriceInCents() {
    let productPriceInCents = 0;

    if (this.currentVariant && this.currentVariant.price !== undefined) {
      const priceStr = this.currentVariant.price.toString();
      if (priceStr.includes(".")) {
        productPriceInCents = Math.round(parseFloat(priceStr) * 100);
      } else {
        productPriceInCents = parseFloat(this.currentVariant.price);
      }
    }

    this.updateWarrantyPricesFromProductPrice(productPriceInCents);

    return productPriceInCents;
  }

  getProductComparePriceInCents() {
    if (
      this.currentVariant &&
      this.currentVariant.compare_at_price !== undefined &&
      this.currentVariant.compare_at_price !== null &&
      this.currentVariant.compare_at_price !== ""
    ) {
      const comparePriceStr = this.currentVariant.compare_at_price.toString();
      if (comparePriceStr.includes(".")) {
        return Math.round(parseFloat(comparePriceStr) * 100);
      } else {
        return parseFloat(this.currentVariant.compare_at_price);
      }
    }
    return 0;
  }

  updateWarrantyPricesFromProductPrice(productPriceInCents) {
    const threeYearPercentage =
      window.FoxTheme?.settings?.warranty3YearPercentage ||
      window.warrantySettings?.warranty_3year_percentage ||
      10;
    const fiveYearPercentage =
      window.FoxTheme?.settings?.warranty5YearPercentage ||
      window.warrantySettings?.warranty_5year_percentage ||
      15;
    const threeYearPercent = Math.max(0, parseFloat(threeYearPercentage) || 0);
    const fiveYearPercent = Math.max(0, parseFloat(fiveYearPercentage) || 0);
    let threeYearPrice, fiveYearPrice;
    if (productPriceInCents <= 0) {
      threeYearPrice = 0;
      fiveYearPrice = 0;
    } else {
      threeYearPrice = Math.round(
        productPriceInCents * (threeYearPercent / 100)
      );
      fiveYearPrice = Math.round(productPriceInCents * (fiveYearPercent / 100));
      if (threeYearPrice <= 0) {
        threeYearPrice = Math.max(1, Math.round(productPriceInCents * 0.01));
      }
      if (fiveYearPrice <= 0) {
        fiveYearPrice = Math.max(1, Math.round(productPriceInCents * 0.01));
      }
    }
    this.updateWarrantyDisplay({
      threeYear: threeYearPrice,
      fiveYear: fiveYearPrice,
      threeYearFormatted:
        threeYearPrice > 0 ? this.formatPrice(threeYearPrice / 100) : "--",
      fiveYearFormatted:
        fiveYearPrice > 0 ? this.formatPrice(fiveYearPrice / 100) : "--",
    });
  }

  updateWarrantyDisplay(warrantyPrices) {
    this.updateWarrantySelectorPrices(warrantyPrices);
    this.updateBelffinProtectionPlanPrices(warrantyPrices);
    this.updateWhatsCoveredModalPrices(warrantyPrices);
  }
  getSelectedWarrantyPrice() {
    const selectedWarranty = document.querySelector(
      '[name="warranty-product"]:checked'
    );

    if (selectedWarranty) {
      const priceStr = selectedWarranty.dataset.price || "0";
      const price = Number(priceStr) || 0;

      return {
        price: price,
        comparePrice: 0,
        priceFormatted: selectedWarranty.dataset.priceFormatted || "$0.00",
      };
    } else {
      return {
        price: this.warrantyPrice,
        comparePrice: this.warrantyComparePrice,
        priceFormatted: `$${(this.warrantyPrice / 100).toFixed(2)}`,
      };
    }
  }

  formatPrice(priceInDollars) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(priceInDollars);
  }
  updateWarrantySelectorPrices(warrantyPrices) {
    const warrantyItems = document.querySelectorAll("[data-warranty-item]");
    warrantyItems.forEach((item) => {
      const planId = item.dataset.planId;
      const inputElement = item.querySelector(".warranty-selector__input");
      const priceElement = item.querySelector("[data-dynamic-warranty-price]");

      if (inputElement && priceElement) {
        if (planId === "warranty-plan-1") {
          priceElement.textContent = warrantyPrices.threeYearFormatted;
          inputElement.dataset.price = Number(warrantyPrices.threeYear) || 0;
          inputElement.dataset.priceFormatted =
            warrantyPrices.threeYearFormatted;
        } else if (planId === "warranty-plan-2") {
          priceElement.textContent = warrantyPrices.fiveYearFormatted;
          inputElement.dataset.price = Number(warrantyPrices.fiveYear) || 0;
          inputElement.dataset.priceFormatted =
            warrantyPrices.fiveYearFormatted;
        }
      }
    });
  }

  updateBelffinProtectionPlanPrices(warrantyPrices) {
    const threeYearElement = document.querySelector(
      '[data-dynamic-price="3year"]'
    );
    const fiveYearElement = document.querySelector(
      '[data-dynamic-price="5year"]'
    );

    if (threeYearElement) {
      threeYearElement.textContent = warrantyPrices.threeYearFormatted;
    }
    if (fiveYearElement) {
      fiveYearElement.textContent = warrantyPrices.fiveYearFormatted;
    }
  }

  updateWhatsCoveredModalPrices(warrantyPrices) {
    if (
      window.whatsCoveredModalInstance &&
      typeof window.whatsCoveredModalInstance.calculateWarrantyPrices ===
        "function"
    ) {
    }
  }

  dispatchPriceUpdateEvent(prices) {
    const unifiedEvent = new CustomEvent("unified-price:update", {
      detail: prices,
    });
    document.dispatchEvent(unifiedEvent);

    const totalEvent = new CustomEvent("total-price:update", {
      detail: {
        totalPrice: prices.totalPrice,
        totalComparePrice: prices.totalComparePrice,
      },
    });
    document.dispatchEvent(totalEvent);
  }

  updateDOMElements(prices) {
    try {
      const customPriceCurrent = document.querySelector(
        ".product__block.product__block--custom_price .custom-price__current"
      );
      if (customPriceCurrent) {
        customPriceCurrent.textContent = prices.customPrice;
      }

      const customPriceOriginal = document.querySelector(
        ".product__block.product__block--custom_price .custom-price__original"
      );
      if (customPriceOriginal) {
        // 使用 compareCents 通过系统统一的格式化函数来更新，确保有千分位
        if (prices.compareCents && typeof prices.compareCents === "number") {
          if (
            window.FoxTheme &&
            window.FoxTheme.Currency &&
            window.FoxTheme.Currency.formatMoney
          ) {
            const moneyFormat =
              window.FoxTheme.settings?.moneyFormat || "${{amount}}";
            customPriceOriginal.textContent =
              window.FoxTheme.Currency.formatMoney(
                prices.compareCents,
                moneyFormat
              );
          } else {
            // Fallback: 使用 Intl.NumberFormat
            const currency = window.Shopify?.currency?.active || "USD";
            const amount = prices.compareCents / 100;
            const formatted = new Intl.NumberFormat(undefined, {
              style: "currency",
              currency: currency,
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              useGrouping: true,
            }).format(amount);
            customPriceOriginal.textContent = formatted;
          }
        } else {
          customPriceOriginal.textContent = prices.customComparePrice;
        }
      }
    } catch (error) {}
  }

  initializeCurrentPrice() {
    if (this.currentVariant) {
      this.calculateAndUpdateAllPrices();
      return;
    }

    if (
      window.product &&
      window.product.newMedia &&
      window.product.newMedia.currentCombination
    ) {
      const variant = window.product.newMedia.currentCombination.variant;
      if (variant) {
        this.currentVariant = variant;
        this.lastProcessedVariantId = variant.id;
        this.calculateAndUpdateAllPrices();
        return;
      }
    }

    if (
      window.product &&
      window.product.variants &&
      window.product.variants.length > 0
    ) {
      const firstVariant = window.product.variants[0];
      this.currentVariant = firstVariant;
      this.lastProcessedVariantId = firstVariant.id;
      this.calculateAndUpdateAllPrices();
      return;
    }
  }

  getCurrentPrices() {
    return this.calculatePrices();
  }

  triggerPriceUpdate() {
    this.calculateAndUpdateAllPrices();
  }

  setVariantData(variant) {
    this.currentVariant = variant;
    this.lastProcessedVariantId = variant ? variant.id : null;
    this.calculateAndUpdateAllPrices();
  }

  getCurrentVariant() {
    return this.currentVariant;
  }

  isEventFromCartDrawer(event) {
    if (!event.target) return false;

    const cartDrawer = event.target.closest("cart-drawer");
    if (cartDrawer) {
      return true;
    }

    const cartPage = event.target.closest(".cart");
    if (cartPage && !cartPage.classList.contains("is-empty")) {
      return true;
    }

    const mainCart = event.target.closest("main-cart");
    if (mainCart) {
      return true;
    }

    const cartItem = event.target.closest(".cart-item");
    if (cartItem) {
      return true;
    }

    if (window.location.pathname.includes("/cart")) {
      return true;
    }

    return false;
  }
}

const UnifiedPriceManagerClass = UnifiedPriceManager;
window.UnifiedPriceManager = class UnifiedPriceManagerStatic {
  static getCurrentPrices() {
    if (window.unifiedPriceManager) {
      return window.unifiedPriceManager.getCurrentPrices();
    }
    return null;
  }

  static triggerPriceUpdate() {
    if (window.unifiedPriceManager) {
      return window.unifiedPriceManager.triggerPriceUpdate();
    }
  }

  static setVariantData(variant) {
    if (window.unifiedPriceManager) {
      return window.unifiedPriceManager.setVariantData(variant);
    }
  }

  static getCurrentVariant() {
    if (window.unifiedPriceManager) {
      return window.unifiedPriceManager.getCurrentVariant();
    }
    return null;
  }

  static registerComponent(type, element) {
    if (window.unifiedPriceManager) {
      return window.unifiedPriceManager.registerComponent(type, element);
    }
  }

  static calculateWarrantyPrices() {
    if (window.unifiedPriceManager) {
      return window.unifiedPriceManager.calculateWarrantyPrices();
    }
    return null;
  }

  static updateWarrantyPrices() {
    if (window.unifiedPriceManager) {
      return window.unifiedPriceManager.triggerPriceUpdate();
    }
  }
};

let isUnifiedPriceManagerInitialized = false;
function initializeUnifiedPriceManager() {
  if (isUnifiedPriceManagerInitialized) {
    return;
  }
  window.unifiedPriceManager = new UnifiedPriceManagerClass();
  isUnifiedPriceManagerInitialized = true;
}

document.addEventListener("DOMContentLoaded", () => {
  initializeUnifiedPriceManager();
});

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initializeUnifiedPriceManager();
  });
} else {
  initializeUnifiedPriceManager();
}

if (!window.unifiedPriceManager && !isUnifiedPriceManagerInitialized) {
  initializeUnifiedPriceManager();
}
window.calculateWarrantyPrices = function () {
  if (
    window.unifiedPriceManager &&
    typeof window.unifiedPriceManager.calculateWarrantyPrices === "function"
  ) {
    return window.unifiedPriceManager.calculateWarrantyPrices();
  }
  return null;
};
window.printAllDataSources = function () {};
